import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useDeckNavigation from '../hooks/useDeckNavigation';

const PARTNER_EMAIL = 'partners@10thirty.co.za';

function buildSlides(t) {
  return [
    {
      id: 'cover',
      title: t('partnerDeck.titles.cover'),
      render: () => (
        <div className="pitch-slide pitch-cover">
          <div className="pitch-cover-chip">{t('partnerDeck.cover.chip')}</div>
          <img src="/logo512.png" alt="10 Thirty" className="pitch-cover-logo" />
          <h1 className="pitch-cover-title">{t('partnerDeck.cover.title')}</h1>
          <p className="pitch-cover-tagline">{t('partnerDeck.cover.tagline')}</p>
          <p className="pitch-cover-sub">{t('partnerDeck.cover.sub')}</p>
        </div>
      ),
    },

    {
      id: 'product',
      title: t('partnerDeck.titles.product'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.product.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.product.heading')}</h2>
          <div className="pitch-body">
            <p>{t('partnerDeck.product.body')}</p>
          </div>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🔐</span>
              <span className="pitch-feature-title">{t('partnerDeck.product.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.product.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">⌛</span>
              <span className="pitch-feature-title">{t('partnerDeck.product.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.product.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🆘</span>
              <span className="pitch-feature-title">{t('partnerDeck.product.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.product.feat3Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🕊️</span>
              <span className="pitch-feature-title">{t('partnerDeck.product.feat4Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.product.feat4Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'audience',
      title: t('partnerDeck.titles.audience'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.audience.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.audience.heading')}</h2>
          <div className="pitch-body">
            <p>{t('partnerDeck.audience.body')}</p>
          </div>
          <div className="pitch-features">
            <div className="pitch-feature">
              <span className="pitch-feature-icon">📰</span>
              <span className="pitch-feature-title">{t('partnerDeck.audience.feat1Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.audience.feat1Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">⚖️</span>
              <span className="pitch-feature-title">{t('partnerDeck.audience.feat2Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.audience.feat2Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">💬</span>
              <span className="pitch-feature-title">{t('partnerDeck.audience.feat3Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.audience.feat3Desc')}</span>
            </div>
            <div className="pitch-feature">
              <span className="pitch-feature-icon">🛡️</span>
              <span className="pitch-feature-title">{t('partnerDeck.audience.feat4Title')}</span>
              <span className="pitch-feature-desc">{t('partnerDeck.audience.feat4Desc')}</span>
            </div>
          </div>
        </div>
      ),
    },

    {
      id: 'offer',
      title: t('partnerDeck.titles.offer'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.offer.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.offer.heading')}</h2>
          <div className="pitch-kpi-grid">
            <div className="pitch-kpi pitch-kpi-highlight">
              <span className="pitch-kpi-label">{t('partnerDeck.offer.kpi1Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.offer.kpi1Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.offer.kpi1Copy')}</span>
            </div>
            <div className="pitch-kpi">
              <span className="pitch-kpi-label">{t('partnerDeck.offer.kpi2Label')}</span>
              <strong className="pitch-kpi-value">10–40%</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.offer.kpi2Copy')}</span>
            </div>
            <div className="pitch-kpi">
              <span className="pitch-kpi-label">{t('partnerDeck.offer.kpi3Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.offer.kpi3Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.offer.kpi3Copy')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('partnerDeck.offer.body')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'tiers',
      title: t('partnerDeck.titles.tiers'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.tiers.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.tiers.heading')}</h2>
          <div className="partner-tier-ladder">
            <div className="partner-tier-row tier-bronze">
              <span className="partner-tier-rate">10%</span>
              <span className="partner-tier-name">Bronze</span>
              <span className="partner-tier-thresh">{t('partnerDeck.tiers.bronzeThresh')}</span>
            </div>
            <div className="partner-tier-row tier-silver">
              <span className="partner-tier-rate">20%</span>
              <span className="partner-tier-name">Silver</span>
              <span className="partner-tier-thresh">{t('partnerDeck.tiers.silverThresh')}</span>
            </div>
            <div className="partner-tier-row tier-gold">
              <span className="partner-tier-rate">30%</span>
              <span className="partner-tier-name">Gold</span>
              <span className="partner-tier-thresh">{t('partnerDeck.tiers.goldThresh')}</span>
            </div>
            <div className="partner-tier-row tier-platinum">
              <span className="partner-tier-rate">40%</span>
              <span className="partner-tier-name">Platinum</span>
              <span className="partner-tier-thresh">{t('partnerDeck.tiers.platinumThresh')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('partnerDeck.tiers.body')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'math',
      title: t('partnerDeck.titles.math'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.math.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.math.heading')}</h2>
          <div className="pitch-kpi-grid">
            <div className="pitch-kpi">
              <span className="pitch-kpi-label">{t('partnerDeck.math.kpi1Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.math.kpi1Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.math.kpi1Copy')}</span>
            </div>
            <div className="pitch-kpi">
              <span className="pitch-kpi-label">{t('partnerDeck.math.kpi2Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.math.kpi2Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.math.kpi2Copy')}</span>
            </div>
            <div className="pitch-kpi pitch-kpi-highlight">
              <span className="pitch-kpi-label">{t('partnerDeck.math.kpi3Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.math.kpi3Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.math.kpi3Copy')}</span>
            </div>
            <div className="pitch-kpi pitch-kpi-wide pitch-kpi-platinum">
              <span className="pitch-kpi-label">{t('partnerDeck.math.kpi4Label')}</span>
              <strong className="pitch-kpi-value">{t('partnerDeck.math.kpi4Value')}</strong>
              <span className="pitch-kpi-copy">{t('partnerDeck.math.kpi4Copy')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('partnerDeck.math.body')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'how-it-works',
      title: t('partnerDeck.titles.how'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.how.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.how.heading')}</h2>
          <div className="pitch-how-it-works">
            <div className="pitch-step">
              <span className="pitch-step-num">01</span>
              <span className="pitch-step-text">{t('partnerDeck.how.step1')}</span>
            </div>
            <div className="pitch-step-arrow">&rarr;</div>
            <div className="pitch-step">
              <span className="pitch-step-num">02</span>
              <span className="pitch-step-text">{t('partnerDeck.how.step2', { email: PARTNER_EMAIL })}</span>
            </div>
            <div className="pitch-step-arrow">&rarr;</div>
            <div className="pitch-step">
              <span className="pitch-step-num">03</span>
              <span className="pitch-step-text">{t('partnerDeck.how.step3')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('partnerDeck.how.body')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'why-now',
      title: t('partnerDeck.titles.whyNow'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.whyNow.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.whyNow.heading')}</h2>
          <div className="pitch-stat-row">
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('partnerDeck.whyNow.stat1Num')}</span>
              <span className="pitch-stat-label">{t('partnerDeck.whyNow.stat1Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('partnerDeck.whyNow.stat2Num')}</span>
              <span className="pitch-stat-label">{t('partnerDeck.whyNow.stat2Label')}</span>
            </div>
            <div className="pitch-stat">
              <span className="pitch-stat-num">{t('partnerDeck.whyNow.stat3Num')}</span>
              <span className="pitch-stat-label">{t('partnerDeck.whyNow.stat3Label')}</span>
            </div>
          </div>
          <div className="pitch-body pitch-body-small">
            <p>{t('partnerDeck.whyNow.body')}</p>
          </div>
        </div>
      ),
    },

    {
      id: 'apply',
      title: t('partnerDeck.titles.apply'),
      render: () => (
        <div className="pitch-slide pitch-content">
          <p className="pitch-label">{t('partnerDeck.apply.label')}</p>
          <h2 className="pitch-heading">{t('partnerDeck.apply.heading')}</h2>
          <div className="partner-apply-card">
            <p className="partner-apply-to">
              <span className="partner-apply-label">{t('partnerDeck.apply.emailLabel')}</span>
              <a href={`mailto:${PARTNER_EMAIL}?subject=10%20Thirty%20Partner%20Application`} className="partner-apply-email">
                {PARTNER_EMAIL}
              </a>
            </p>
            <ul className="partner-apply-checklist">
              <li>{t('partnerDeck.apply.check1')}</li>
              <li>{t('partnerDeck.apply.check2')}</li>
              <li>{t('partnerDeck.apply.check3')}</li>
              <li>{t('partnerDeck.apply.check4')}</li>
            </ul>
          </div>
          <div className="pitch-contact">
            <p>{t('partnerDeck.apply.contact')}</p>
          </div>
        </div>
      ),
    },
  ];
}

export default function PartnerDeck() {
  const { t } = useTranslation();
  const slides = buildSlides(t);
  const total = slides.length;
  const { current, prev, next, stageProps } = useDeckNavigation(total);

  const slide = slides[current];

  return (
    <div className="pitch-deck">
      <div className="pitch-stage" {...stageProps}>
        {slide.render()}
      </div>

      <div className="pitch-controls">
        <Link to="/" className="pitch-home-link" aria-label={t('partnerDeck.controls.backToSite')}>
          <img src="/logo192.png" alt="10 Thirty" className="pitch-home-logo" />
        </Link>
        <button
          className="pitch-nav-btn"
          onClick={prev}
          disabled={current === 0}
          aria-label={t('partnerDeck.controls.prevSlide')}
        >
          &larr;
        </button>
        <div className="pitch-counter-wrap">
          <span className="pitch-counter-label">{slide.title}</span>
          <span className="pitch-counter">{current + 1} / {total}</span>
        </div>
        <button
          className="pitch-nav-btn"
          onClick={next}
          disabled={current === total - 1}
          aria-label={t('partnerDeck.controls.nextSlide')}
        >
          &rarr;
        </button>
      </div>

      <div className="pitch-progress-track">
        <div
          className="pitch-progress-fill"
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );
}
